import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Button, Checkbox, TextInput } from '@carbon/react';
import { Add, Flag, Movement, TrashCan } from '@carbon/react/icons';
import { AddressReviewFlag } from './address-review-flag';
import { IconAction } from './blocked-action';
import type {
  EntityAddress,
  EntityPhoneNumber,
  ReviewFlag,
  StructuredPartyDetail,
} from './types';
import { moveAddressBlockedReason } from './party-status';
import { anchorCard, anchorName, cardAddresses } from './utils';
import style from './staged-entity-editor.module.scss';

export interface AddressEntry {
  id: number | null;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  asFoundAddress: string | null;
  reviewFlag: ReviewFlag | null;
  primary: boolean;
  destroy: boolean;
}

export interface PhoneEntry {
  id: number | null;
  number: string;
  primary: boolean;
  destroy: boolean;
}

export interface StagedEntityFormState {
  name: string;
  role: string;
  addresses: AddressEntry[];
  phones: PhoneEntry[];
}

export interface StagedEntityEditorHandle {
  buildPatchPayload: () => {
    name?: string;
    role?: string;
    addresses_attributes?: Array<Record<string, unknown>>;
    phone_numbers_attributes?: Array<Record<string, unknown>>;
    hasChanges: boolean;
  };
  // The anchor the form was seeded from: its changes are against that anchor.
  seededAnchorId: number | undefined;
  reset: () => void;
}

interface Props {
  party: StructuredPartyDetail;
  disabled?: boolean;
  onChange?: (state: StagedEntityFormState) => void;
  // Whether the unsaved edits touch the anchor's own fields (name, addresses,
  // phone numbers), which every party sharing the anchor shows.
  onSharedEditChange?: (touchesShared: boolean) => void;
  /**
   * Called when the user clicks the per-address Move button. Parent owns the
   * destination picker (the MoveAddressModal) and the actual API call.
   */
  onMoveAddress?: (addressId: number) => void;
  /**
   * Called when the user clicks the per-address Clear Flag button. Parent owns
   * the API call; the editor optimistically clears the flag in local state.
   */
  onClearFlag?: (addressId: number) => void;
}

/**
 * An empty address row.
 *
 * @param {boolean} primary - Whether the row starts as the primary address.
 * @return {AddressEntry} The blank row.
 */
const blankAddress = (primary = false): AddressEntry => ({
  id: null,
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
  asFoundAddress: null,
  reviewFlag: null,
  primary,
  destroy: false,
});

/**
 * An empty phone number row.
 *
 * @param {boolean} primary - Whether the row starts as the primary number.
 * @return {PhoneEntry} The blank row.
 */
const blankPhone = (primary = false): PhoneEntry => ({
  id: null,
  number: '',
  primary,
  destroy: false,
});

/**
 * Turns a saved address into a form row.
 *
 * @param {EntityAddress} address - The saved address.
 * @param {boolean} primary - Whether the row is the primary address.
 * @return {AddressEntry} The form row.
 */
const fromEntityAddress = (
  address: EntityAddress,
  primary: boolean
): AddressEntry => ({
  id: address.id,
  streetAddress: address.streetAddress ?? '',
  city: address.city ?? '',
  state: address.state ?? '',
  zipCode: address.zipCode ?? '',
  asFoundAddress: address.asFoundAddress ?? null,
  reviewFlag: address.reviewFlag ?? null,
  primary,
  destroy: false,
});

/**
 * Turns a saved phone number into a form row.
 *
 * @param {EntityPhoneNumber} phone - The saved phone number.
 * @return {PhoneEntry} The form row.
 */
const fromEntityPhone = (phone: EntityPhoneNumber): PhoneEntry => ({
  id: phone.id,
  number: phone.number ?? '',
  primary: !!phone.primary,
  destroy: false,
});

/**
 * The form's starting state. Name, address and phone edits land on the
 * party's current anchor, so the editor edits the anchor's records (the
 * staged entity's until it is matched). Role belongs to the party on this
 * document.
 *
 * @param {StructuredPartyDetail} party - The party being edited.
 * @return {StagedEntityFormState} The seeded form state.
 */
const initialState = (party: StructuredPartyDetail): StagedEntityFormState => {
  const staged = anchorCard(party);
  const addrs = cardAddresses(staged);
  const addresses =
    addrs.length === 0
      ? [blankAddress(true)]
      : addrs.map((a, i) => fromEntityAddress(a, i === 0));

  const phoneRecords: EntityPhoneNumber[] = staged?.phoneNumbers ?? [];
  const phones = phoneRecords.map((p) => fromEntityPhone(p));

  return {
    name: anchorName(party),
    role: party.role ?? '',
    addresses,
    phones,
  };
};

/**
 * Copies a list with one item's fields patched.
 *
 * @param {T[]} items - The list.
 * @param {number} index - The item to patch.
 * @param {Partial<T>} patch - The fields to change.
 * @return {T[]} The patched copy.
 */
function patchAt<T>(items: T[], index: number, patch: Partial<T>): T[] {
  return items.map((item, i) => {
    if (i !== index) return item;
    return { ...item, ...patch };
  });
}

/**
 * Toggles one row's primary flag. Unchecking leaves no primary, which the
 * model's "≤1 primary" constraint allows; checking one unchecks the rest.
 *
 * @param {T[]} items - The rows.
 * @param {number} index - The row to toggle.
 * @return {T[]} The updated copy.
 */
function togglePrimaryAt<T extends { primary: boolean }>(
  items: T[],
  index: number
): T[] {
  if (items[index]?.primary) {
    return items.map((item, i) => {
      if (i !== index) return item;
      return { ...item, primary: false };
    });
  }
  return items.map((item, i) => ({ ...item, primary: i === index }));
}

/**
 * The form's seed for a party: its starting state, a key that changes
 * whenever that state does, and the anchor it was read from.
 *
 * @param {StructuredPartyDetail} party - The party being edited.
 * @return {{ original: StagedEntityFormState, key: string, anchorId: number | undefined }} The seed.
 */
const seedFor = (party: StructuredPartyDetail) => {
  const original = initialState(party);
  return {
    original,
    key: JSON.stringify(original),
    anchorId: party.currentAnchor?.id,
  };
};

/**
 * Whether two address rows hold the same values, ignoring surrounding
 * whitespace.
 *
 * @param {AddressEntry} a - One row.
 * @param {AddressEntry} b - The other row.
 * @return {boolean} True when they match.
 */
const addressEqual = (a: AddressEntry, b: AddressEntry) =>
  a.streetAddress.trim() === b.streetAddress.trim() &&
  a.city.trim() === b.city.trim() &&
  a.state.trim() === b.state.trim() &&
  a.zipCode.trim() === b.zipCode.trim() &&
  a.primary === b.primary &&
  a.destroy === b.destroy;

/**
 * Whether two phone rows hold the same values, ignoring surrounding
 * whitespace.
 *
 * @param {PhoneEntry} a - One row.
 * @param {PhoneEntry} b - The other row.
 * @return {boolean} True when they match.
 */
const phoneEqual = (a: PhoneEntry, b: PhoneEntry) =>
  a.number.trim() === b.number.trim() &&
  a.primary === b.primary &&
  a.destroy === b.destroy;

/**
 * Whether an address row has any field filled in.
 *
 * @param {AddressEntry} a - The row.
 * @return {boolean} True when a field is non-blank.
 */
const addressHasContent = (a: AddressEntry) =>
  [a.streetAddress, a.city, a.state, a.zipCode].some((v) => v.trim() !== '');

/**
 * Whether a phone row has a number filled in.
 *
 * @param {PhoneEntry} p - The row.
 * @return {boolean} True when the number is non-blank.
 */
const phoneHasContent = (p: PhoneEntry) => p.number.trim() !== '';

interface SavedRows {
  addresses: Map<number, AddressEntry>;
  phones: Map<number, PhoneEntry>;
}

/**
 * Indexes saved rows by id, keeping the first row for each id.
 *
 * @param {T[]} rows - The rows.
 * @return {Map<number, T>} The saved rows by id.
 */
const indexById = <T extends { id: number | null }>(rows: T[]) => {
  const byId = new Map<number, T>();
  rows.forEach((row) => {
    if (row.id !== null && !byId.has(row.id)) byId.set(row.id, row);
  });
  return byId;
};

// The saved rows by id, built once per seeded form state: both the per-keystroke
// shared-edit check and the payload read it, and anchors carry ~1k addresses.
const savedRowsIndex = new WeakMap<StagedEntityFormState, SavedRows>();
/**
 * The saved rows of a seeded form state by id, built once and cached.
 *
 * @param {StagedEntityFormState} original - The seeded form state.
 * @return {SavedRows} Its addresses and phones by id.
 */
const savedRowsOf = (original: StagedEntityFormState): SavedRows => {
  let saved = savedRowsIndex.get(original);
  if (!saved) {
    saved = {
      addresses: indexById(original.addresses),
      phones: indexById(original.phones),
    };
    savedRowsIndex.set(original, saved);
  }
  return saved;
};

type RowChange = 'create' | 'destroy' | 'update' | null;

/**
 * What saving would do with a form row, measured against its saved version.
 *
 * @param {T} row - The form row.
 * @param {Map<number, T>} saved - The saved rows by id.
 * @param {Function} hasContent - Whether a new row has anything to save.
 * @param {Function} equal - Whether a row matches its saved version.
 * @return {RowChange} The change, or null when there is none.
 */
const rowChange = <T extends { id: number | null; destroy: boolean }>(
  row: T,
  saved: Map<number, T>,
  hasContent: (row: T) => boolean,
  equal: (a: T, b: T) => boolean
): RowChange => {
  if (row.id === null) return hasContent(row) ? 'create' : null;
  if (row.destroy) return 'destroy';
  const before = saved.get(row.id);
  return before && !equal(before, row) ? 'update' : null;
};

/**
 * Whether buildPatchPayload would send a name, address or phone change. Runs
 * on every keystroke, so it stops at the first difference instead of building
 * the payload.
 *
 * @param {StagedEntityFormState} original - The seeded form state.
 * @param {StagedEntityFormState} state - The current form state.
 * @return {boolean} True when an anchor field would change.
 */
function touchesSharedFields(
  original: StagedEntityFormState,
  state: StagedEntityFormState
): boolean {
  if (state.name.trim() !== original.name.trim()) return true;
  const saved = savedRowsOf(original);
  return (
    state.addresses.some(
      (a) =>
        rowChange(a, saved.addresses, addressHasContent, addressEqual) !== null
    ) ||
    state.phones.some(
      (p) => rowChange(p, saved.phones, phoneHasContent, phoneEqual) !== null
    )
  );
}

const SeededEditor = forwardRef<StagedEntityEditorHandle, Props>(
  (
    {
      party,
      disabled,
      onChange,
      onSharedEditChange,
      onMoveAddress,
      onClearFlag,
    },
    ref
  ) => {
    const [seed, setSeed] = useState(() => seedFor(party));
    const [state, setState] = useState<StagedEntityFormState>(seed.original);
    const { original } = seed;

    // Saved details (this examiner's Update, or another examiner's edit) arrive
    // with the refreshed party. They replace the form unless the examiner is
    // mid-edit with changes the refresh doesn't already contain.
    const incoming = seedFor(party);
    if (incoming.key !== seed.key) {
      const keepEdits =
        !disabled &&
        buildPatchPayload(original, state).hasChanges &&
        buildPatchPayload(incoming.original, state).hasChanges;
      if (keepEdits) {
        setSeed({ ...seed, key: incoming.key });
      } else {
        setSeed(incoming);
        setState(incoming.original);
      }
    }

    // A review flag cleared elsewhere (e.g. from the overview table) shows up
    // without resetting the examiner's in-progress text edits.
    const latestFlags = new Map(
      cardAddresses(anchorCard(party)).map((a) => [a.id, a.reviewFlag ?? null])
    );
    /**
     * The latest review flag for a row, preferring the refreshed party's.
     *
     * @param {AddressEntry} entry - The address row.
     * @return {ReviewFlag | null} The row's flag, if any.
     */
    const flagFor = (entry: AddressEntry) => {
      if (entry.id === null || !latestFlags.has(entry.id)) {
        return entry.reviewFlag;
      }
      return latestFlags.get(entry.id) ?? null;
    };

    /**
     * Applies a state change and reports the new state.
     *
     * @param {Function} next - Derives the next state from the current one.
     * @return {void}
     */
    const update = (
      next: (current: StagedEntityFormState) => StagedEntityFormState
    ) => {
      const nextState = next(state);
      setState(nextState);
      onChange?.(nextState);
    };

    /**
     * Sets the name.
     *
     * @param {string} v - The new name.
     */
    const setName = (v: string) => update((s) => ({ ...s, name: v }));
    /**
     * Sets the role.
     *
     * @param {string} v - The new role.
     */
    const setRole = (v: string) => update((s) => ({ ...s, role: v }));

    /**
     * Patches one address row.
     *
     * @param {number} index - The row.
     * @param {Partial<AddressEntry>} patch - The fields to change.
     */
    const updateAddress = (index: number, patch: Partial<AddressEntry>) =>
      update((s) => ({
        ...s,
        addresses: patchAt(s.addresses, index, patch),
      }));

    /**
     * Appends a blank address row.
     */
    const addAddress = () =>
      update((s) => ({
        ...s,
        addresses: [...s.addresses, blankAddress(false)],
      }));

    /**
     * Removes an address row: an unsaved row is dropped, a saved one is marked
     * for destroy. A primary address is kept whenever a row remains.
     *
     * @param {number} index - The row.
     */
    const removeAddress = (index: number) =>
      update((s) => {
        const target = s.addresses[index];
        if (target.id === null) {
          // unsaved row — drop it locally
          const next = s.addresses.filter((_, i) => i !== index);
          if (next.length === 0) next.push(blankAddress(true));
          if (!next.some((a) => a.primary)) next[0].primary = true;
          return { ...s, addresses: next };
        }
        // existing row — mark for destroy
        const next = patchAt(s.addresses, index, {
          destroy: true,
          primary: false,
        });
        if (!next.some((a) => a.primary && !a.destroy)) {
          const firstAlive = next.findIndex((a) => !a.destroy);
          if (firstAlive >= 0)
            next[firstAlive] = { ...next[firstAlive], primary: true };
        }
        return { ...s, addresses: next };
      });

    /**
     * Toggles an address row's primary flag.
     *
     * @param {number} index - The row.
     */
    const togglePrimary = (index: number) =>
      update((s) => ({
        ...s,
        addresses: togglePrimaryAt(s.addresses, index),
      }));

    /**
     * Patches one phone row.
     *
     * @param {number} index - The row.
     * @param {Partial<PhoneEntry>} patch - The fields to change.
     */
    const updatePhone = (index: number, patch: Partial<PhoneEntry>) =>
      update((s) => ({
        ...s,
        phones: patchAt(s.phones, index, patch),
      }));

    /**
     * Appends a blank phone row, primary when it is the first.
     */
    const addPhone = () =>
      update((s) => ({
        ...s,
        phones: [...s.phones, blankPhone(s.phones.length === 0)],
      }));

    /**
     * Removes a phone row: an unsaved row is dropped, a saved one is marked for
     * destroy.
     *
     * @param {number} index - The row.
     */
    const removePhone = (index: number) =>
      update((s) => {
        const target = s.phones[index];
        if (target.id === null) {
          return { ...s, phones: s.phones.filter((_, i) => i !== index) };
        }
        return {
          ...s,
          phones: patchAt(s.phones, index, { destroy: true, primary: false }),
        };
      });

    /**
     * Toggles a phone row's primary flag.
     *
     * @param {number} index - The row.
     */
    const togglePhonePrimary = (index: number) =>
      update((s) => ({
        ...s,
        phones: togglePrimaryAt(s.phones, index),
      }));

    const moveBlocked = moveAddressBlockedReason(party);

    /**
     * Asks the parent to move a saved address.
     *
     * @param {number} index - The row.
     */
    const handleMoveAddress = (index: number) => {
      const target = state.addresses[index];
      if (target.id !== null && onMoveAddress) onMoveAddress(target.id);
    };

    /**
     * Asks the parent to clear a saved address's review flag.
     *
     * @param {number} index - The row.
     */
    const handleClearFlag = (index: number) => {
      const target = state.addresses[index];
      if (target.id !== null && onClearFlag) onClearFlag(target.id);
    };

    const touchesShared = touchesSharedFields(original, state);
    useEffect(() => {
      onSharedEditChange?.(touchesShared);
    }, [touchesShared, onSharedEditChange]);

    useImperativeHandle(
      ref,
      () => ({
        buildPatchPayload: () => buildPatchPayload(original, state),
        seededAnchorId: seed.anchorId,
        reset: () => {
          setState(original);
          onChange?.(original);
        },
      }),
      [original, seed.anchorId, state, onChange]
    );

    const visibleAddresses = useMemo(
      () =>
        state.addresses
          .map((a, i) => ({ entry: a, index: i }))
          .filter((x) => !x.entry.destroy),
      [state.addresses]
    );

    const visiblePhones = useMemo(
      () =>
        state.phones
          .map((p, i) => ({ entry: p, index: i }))
          .filter((x) => !x.entry.destroy),
      [state.phones]
    );

    return (
      <div className={style.form}>
        <div className={style.field}>
          <TextInput
            id="staged-name"
            labelText="Name"
            value={state.name}
            disabled={disabled}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className={style.field}>
          <TextInput
            id="staged-role"
            labelText="Role"
            value={state.role}
            disabled={disabled}
            onChange={(e) => setRole(e.target.value)}
          />
        </div>

        <div className={style.divider} />

        <div className={style.addressList}>
          {visibleAddresses.map(({ entry, index }, position) => {
            const reviewFlag = flagFor(entry);
            return (
              <div key={index} className={style.addressCard}>
                <div className={style.addressHeader}>
                  <div className={style.addressTitle}>
                    <span>Address {position + 1}</span>
                    {reviewFlag && <AddressReviewFlag flag={reviewFlag} />}
                  </div>
                  <div className={style.addressActions}>
                    {reviewFlag && entry.id !== null && onClearFlag && (
                      <IconAction
                        label="Clear Flag"
                        renderIcon={Flag}
                        disabled={disabled}
                        onClick={() => handleClearFlag(index)}
                      />
                    )}
                    {onMoveAddress && entry.id !== null && (
                      <IconAction
                        label="Move Address"
                        renderIcon={Movement}
                        blockedReason={moveBlocked}
                        disabled={disabled}
                        onClick={() => handleMoveAddress(index)}
                      />
                    )}
                    <IconAction
                      label="Remove Address"
                      renderIcon={TrashCan}
                      disabled={disabled}
                      onClick={() => removeAddress(index)}
                    />
                  </div>
                </div>

                {entry.asFoundAddress && (
                  <div className={style.asFound}>
                    as-found: {entry.asFoundAddress}
                  </div>
                )}

                <div className={style.addressFieldsGrid}>
                  <TextInput
                    id={`staged-street-${index}`}
                    labelText="Street Address"
                    value={entry.streetAddress}
                    disabled={disabled}
                    onChange={(e) =>
                      updateAddress(index, { streetAddress: e.target.value })
                    }
                    className={style.fullWidth}
                  />
                  <TextInput
                    id={`staged-city-${index}`}
                    labelText="City"
                    value={entry.city}
                    disabled={disabled}
                    onChange={(e) =>
                      updateAddress(index, { city: e.target.value })
                    }
                  />
                  <TextInput
                    id={`staged-state-${index}`}
                    labelText="State"
                    value={entry.state}
                    disabled={disabled}
                    onChange={(e) =>
                      updateAddress(index, { state: e.target.value })
                    }
                  />
                  <TextInput
                    id={`staged-zip-${index}`}
                    labelText="ZIP"
                    value={entry.zipCode}
                    disabled={disabled}
                    onChange={(e) =>
                      updateAddress(index, { zipCode: e.target.value })
                    }
                    className={style.fullWidth}
                  />
                </div>

                <div className={style.primaryRow}>
                  <Checkbox
                    id={`staged-primary-${index}`}
                    labelText="Primary"
                    checked={entry.primary}
                    disabled={disabled}
                    onChange={() => togglePrimary(index)}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Add}
          onClick={addAddress}
          disabled={disabled}
          className={style.addAddress}
        >
          Add Address
        </Button>

        <div className={style.divider} />

        <div className={style.phoneList}>
          {visiblePhones.map(({ entry, index }, position) => (
            <div key={index} className={style.phoneCard}>
              <div className={style.phoneHeader}>
                <span>Phone Number {position + 1}</span>
                <IconAction
                  label="Remove Phone"
                  renderIcon={TrashCan}
                  disabled={disabled}
                  onClick={() => removePhone(index)}
                />
              </div>
              <TextInput
                id={`staged-phone-${index}`}
                labelText="Number"
                value={entry.number}
                disabled={disabled}
                onChange={(e) => updatePhone(index, { number: e.target.value })}
              />
              <div className={style.primaryRow}>
                <Checkbox
                  id={`staged-phone-primary-${index}`}
                  labelText="Primary"
                  checked={entry.primary}
                  disabled={disabled}
                  onChange={() => togglePhonePrimary(index)}
                />
              </div>
            </div>
          ))}
        </div>

        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Add}
          onClick={addPhone}
          disabled={disabled}
          className={style.addAddress}
        >
          Add Phone Number
        </Button>
      </div>
    );
  }
);
SeededEditor.displayName = 'SeededEditor';

// Seeds from the party's anchor; a different party or anchor starts a fresh
// form.
export const StagedEntityEditor = forwardRef<StagedEntityEditorHandle, Props>(
  (props, ref) => {
    const { party } = props;
    const anchorId = anchorCard(party)?.id;
    return (
      <SeededEditor
        key={`${party.kind}-${party.id}-${anchorId}`}
        ref={ref}
        {...props}
      />
    );
  }
);
StagedEntityEditor.displayName = 'StagedEntityEditor';

/**
 * The PATCH body for the form's changes against its seeded state, with
 * whether there are any.
 *
 * @param {StagedEntityFormState} original - The seeded form state.
 * @param {StagedEntityFormState} state - The current form state.
 * @return {ReturnType<typeof buildPatchPayload>} The payload.
 */
function buildPatchPayload(
  original: StagedEntityFormState,
  state: StagedEntityFormState
): {
  name?: string;
  role?: string;
  addresses_attributes?: Array<Record<string, unknown>>;
  phone_numbers_attributes?: Array<Record<string, unknown>>;
  hasChanges: boolean;
} {
  const payload: ReturnType<typeof buildPatchPayload> = { hasChanges: false };
  const trimmedName = state.name.trim();
  if (trimmedName !== original.name.trim()) {
    payload.name = trimmedName;
    payload.hasChanges = true;
  }
  if (state.role.trim() !== original.role.trim()) {
    payload.role = state.role.trim();
    payload.hasChanges = true;
  }

  const saved = savedRowsOf(original);
  const addressAttrs: Array<Record<string, unknown>> = [];
  state.addresses.forEach((a) => {
    const change = rowChange(
      a,
      saved.addresses,
      addressHasContent,
      addressEqual
    );
    if (change === null) return;
    if (change === 'destroy') {
      addressAttrs.push({ id: a.id, _destroy: true });
      return;
    }
    const trimmed = {
      street_address: a.streetAddress.trim() || null,
      city: a.city.trim() || null,
      state: a.state.trim() || null,
      zip_code: a.zipCode.trim() || null,
      primary: a.primary,
    };
    addressAttrs.push(change === 'create' ? trimmed : { id: a.id, ...trimmed });
  });
  if (addressAttrs.length > 0) {
    payload.addresses_attributes = addressAttrs;
    payload.hasChanges = true;
  }

  const phoneAttrs: Array<Record<string, unknown>> = [];
  state.phones.forEach((p) => {
    const change = rowChange(p, saved.phones, phoneHasContent, phoneEqual);
    if (change === null) return;
    if (change === 'destroy') {
      phoneAttrs.push({ id: p.id, _destroy: true });
      return;
    }
    const trimmed = { number: p.number.trim() || null, primary: p.primary };
    phoneAttrs.push(change === 'create' ? trimmed : { id: p.id, ...trimmed });
  });
  if (phoneAttrs.length > 0) {
    payload.phone_numbers_attributes = phoneAttrs;
    payload.hasChanges = true;
  }

  return payload;
}
